import { Component, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DataPoint } from '../../providers/data-visualization/data-visualization.service';
import { DataCategory } from '../data-category-chips/data-category-chips.component';

Chart.register(zoomPlugin);

@Component({
  selector: 'app-data-visualization-chart',
  templateUrl: './data-visualization-chart.component.html',
  styleUrls: ['./data-visualization-chart.component.scss'],
  standalone: false,
})
export class DataVisualizationChartComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() dataPoints: DataPoint[] = [];
  @Input() categories: DataCategory[] = [];
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;

  private chart!: Chart<any>;
  private outsideTapHandler!: (e: Event) => void;
  private langSub!: Subscription;
  hasData = false;

  constructor(private translate: TranslateService) {}

  ngAfterViewInit() {
    if (this.hasData && this.chartCanvas) this.createChart();
    this.langSub = this.translate.onLangChange.subscribe(() => this.updateChart());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataPoints'] || changes['categories']) {
      this.validateData();
      if (this.chart) {
        this.updateChart();
      }
    }
  }

  private validateData(): void {
    this.hasData = this.dataPoints?.length > 0 && this.categories?.length > 0;
  }

  private formatDateTime(timestamp: number, includeTime: boolean = true): string {
    const lang = this.translate.currentLang || 'de';
    const date = new Date(timestamp);
    if (!includeTime) {
      return date.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    const dateStr = date.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} ${timeStr}`;
  }

  private wrapTooltipText(text: string, maxLineLength: number = 42): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0)
      return [''];

    const lines: string[] = [];
    let currentLine = words[0];
    for (const word of words.slice(1)) {
      if ((currentLine + ' ' + word).length <= maxLineLength) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  }

  private createChart(): void {
    const ctx = this.chartCanvas?.nativeElement?.getContext('2d');
    if (!ctx) return;

    // build category map efficiently in single pass
    const categoryMap = new Map<string, DataPoint[]>();
    this.dataPoints.forEach(dp => {
      if (!categoryMap.has(dp.categoryId)) {
        categoryMap.set(dp.categoryId, []);
      }
      categoryMap.get(dp.categoryId)!.push(dp);
    });

    // collect all unique timestamps across all data points, sorted chronologically
    const allTimestamps = Array.from(
      new Set(this.dataPoints.map(p => p.timestamp))
    ).sort((a, b) => a - b);

    // collect all unique dates across all data points, sorted chronologically
    const allDates = Array.from(
      new Set(this.dataPoints.map(p => new Date(p.timestamp).toISOString().split('T')[0]))
    ).sort();

    // build datasets with category lookup aligned to allTimestamps
    const datasets = this.categories.map((category, index) => {
      const points = categoryMap.get(category.id) || [];
      const pointMap = new Map<number, number>();
      points.forEach(p => pointMap.set(p.timestamp, p.value));

      const data = allTimestamps.map(ts => ({
        x: ts,
        y: pointMap.has(ts) ? pointMap.get(ts)! : (null as any)
      }));

      const pointStyle = index === 0 ? 'circle' : 'rect';
      const pointRadius = index === 0 ? 3.5 : 6;
      const pointHoverRadius = index === 0 ? 5.5 : 8;

      return {
        label: category.label,
        data,
        borderColor: category.color || '#999999',
        backgroundColor: this.hexToRgba(category.color || '#999999', 0.2),
        tension: 0,
        yAxisID: index === 0 ? 'y' : 'y1',
        fill: false,
        borderWidth: 2,
        pointStyle,
        pointRadius,
        pointHoverRadius,
        pointBackgroundColor: category.color || '#999999',
        spanGaps: true,
      };
    });

    const zoomLimits: any = {};
    const iqrBoundsPerAxis = new Map<string, { min: number; max: number } | null>();
    this.categories.forEach((category, index) => {
      const axisId = index === 0 ? 'y' : 'y1';
      const values = (categoryMap.get(category.id) || []).map(p => p.value).filter(v => v != null && !isNaN(v));
      if (!values.length) return;

      if (category.valueType === 'timestamp') {
        // for date/datetime: use data range with 5% padding so points aren't cut off and reset zoom works
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const range = maxVal - minVal;
        const padding = range > 0 ? range * 0.05 : 24 * 60 * 60 * 1000;
        const axisMin = minVal - padding;
        const axisMax = maxVal + padding;
        zoomLimits[axisId] = { min: axisMin, max: axisMax };
        iqrBoundsPerAxis.set(axisId, { min: axisMin, max: axisMax });
      } else if (category.valueType === 'minutes') {
        // for time questions: allow zoom from 0 to 1440 (24 hours)
        zoomLimits[axisId] = { min: 0, max: 1440 };
      } else {
        // for numeric questions: add padding
        zoomLimits[axisId] = { min: Math.min(...values) - 1, max: Math.max(...values) + 1 };
      }

      if (category.valueType !== 'timestamp') {
        iqrBoundsPerAxis.set(axisId, this.computeIqrBounds(values, category));
      }
    });

    const yAxes: any = {
      y: this.buildYAxisConfig(this.categories[0], 'left', iqrBoundsPerAxis.get('y'))
    };

    if (this.categories.length > 1) {
      yAxes.y1 = this.buildYAxisConfig(
        this.categories[1], 'right',
        iqrBoundsPerAxis.get('y1'),
        { grid: { drawOnChartArea: false } }
      );
    }

    try {
      this.chart = new Chart(ctx, {
        type: 'line',
        data: { labels: allDates, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          onClick: (_event, elements) => {
            if (elements.length === 0) {
              this.chart?.tooltip?.setActiveElements([], { x: 0, y: 0 });
              this.chart?.update();
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              onClick: () => {},
              labels: {
                generateLabels: (chart) => {
                  const original = Chart.defaults.plugins.legend?.labels?.generateLabels;
                  const labels = original ? original(chart) : [];
                  return labels.map(label => {
                    if (label.text && label.text.length > 40) {
                      label.text = label.text.substring(0, 37) + '...';
                    }
                    return label;
                  });
                }
              }
            },
            title: { display: false },
            tooltip: {
              displayColors: false,
              padding: 12,
              filter: (tooltipItem) => {
                return tooltipItem.raw && (tooltipItem.raw as any).y !== null;
              },
              callbacks: {
                title: (items) => {
                  const timestamp = items[0]?.parsed?.x;
                  if (typeof timestamp !== 'number') return '';
                  return this.wrapTooltipText(this.formatDateTime(timestamp, true));
                },
                label: (context) => {
                  const category = this.categories[context.datasetIndex];
                  const formatter = this.makeTickFormatter(category, true);
                  return this.wrapTooltipText(`${context.dataset.label}: ${formatter(context.parsed.y)}`);
                }
              }
            },
            zoom: {
              limits: zoomLimits,
              pan: { enabled: true, mode: 'y' },
              zoom: {
                wheel: { enabled: true, speed: 0.05 },
                pinch: { enabled: true },
                mode: 'y',
              },
            },
          },
          scales: {
            x: {
              type: 'linear',
              title: { display: true, text: this.translate.instant('DATA_VISUALIZATION.ENTRY') },
              ticks: {
                maxRotation: 45,
                callback: (value: any) => {
                  if (typeof value !== 'number') return value;
                  return window.innerWidth < 480 ? this.formatDateTime(value, false).slice(0, 5) : this.formatDateTime(value, false);
                }
              }
            },
            ...yAxes
          }
        }
      });
    } catch (e) {
      console.error('[Chart] ERROR while building Chart constructor:', e);
    }

    // dismiss tooltip when tapping outside the canvas on mobile
    if (this.outsideTapHandler) {
      document.removeEventListener('touchstart', this.outsideTapHandler);
    }
    this.outsideTapHandler = (e: Event) => {
      const canvas = this.chartCanvas?.nativeElement;
      if (canvas && !canvas.contains(e.target as Node)) {
        this.chart?.tooltip?.setActiveElements([], { x: 0, y: 0 });
        this.chart?.update();
      }
    };
    document.addEventListener('touchstart', this.outsideTapHandler);
  }

  private buildYAxisConfig(category: DataCategory | undefined, position: 'left' | 'right', iqrBounds?: { min: number; max: number } | null, extra: any = {}): any {
    const isMinutes = category?.valueType === 'minutes';
    const color = category?.color || '#666666';
    return {
      type: 'linear',
      display: true,
      position,
      title: { display: false },
      ...(isMinutes ? { min: 0, max: 1440 } : {}),
      ...(!isMinutes && iqrBounds ? { min: iqrBounds.min, max: iqrBounds.max } : {}),
      ticks: {
        color,
        callback: this.makeTickFormatter(category, false),
        ...(isMinutes ? { stepSize: 60 } : {}),
      },
      ...extra,
    };
  }

  private computeIqrBounds(values: number[], category?: DataCategory): { min: number; max: number } | null {
    // only focus-clip for timestamps
    if (category?.valueType !== 'timestamp') return null;
    const sorted = [...values].sort((a, b) => a - b);
    if (sorted.length < 3) return null;
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.ceil(sorted.length * 0.75) - 1];
    const iqr = q3 - q1;
    if (iqr === 0) return null;
    const totalRange = sorted[sorted.length - 1] - sorted[0];
    // only apply when the outlier actually distorts the graph (total range >> IQR)
    if (totalRange / iqr < 5) return null;
    const padding = iqr * 0.1;
    return { min: q1 - 1.5 * iqr - padding, max: q3 + 1.5 * iqr + padding };
  }

  private makeTickFormatter(category?: DataCategory, includeTimeForDateTime: boolean = false): (value: any) => string {
    const type = category?.valueType;
    if (type === 'timestamp') {
      const showTime = category?.questionType === 'TextDate_datetime' && includeTimeForDateTime;
      return (value: any) => {
        const d = new Date(value);
        const lang = this.translate.currentLang || 'de';
        if (!showTime) {
          return d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
        }
        const dateStr = d.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timeStr = d.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
        return `${dateStr} ${timeStr}`;
      };
    }
    if (type === 'minutes') {
      return (value: any) => {
        const mins = Math.round(value);
        const h = Math.floor(mins / 60).toString().padStart(2, '0');
        const m = (mins % 60).toString().padStart(2, '0');
        return `${h}:${m}`;
      };
    }
    return (value: any) => {
      const rounded = Math.round(Number(value) * 2) / 2;
      return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1);
    };
  }

  private hexToRgba(color: string, alpha: number): string {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private updateChart(): void {
    if (!this.chart) return;
    this.chart.destroy();
    setTimeout(() => {
      if (this.hasData && this.chartCanvas) {
        this.createChart();
      }
    }, 0);
  }

  resetZoom(): void {
    this.chart?.resetZoom();
  }

  ngOnDestroy() {
    this.langSub?.unsubscribe();
    if (this.outsideTapHandler) {
      document.removeEventListener('touchstart', this.outsideTapHandler);
    }
    this.chart?.destroy();
  }
}
